import { MailOutlined, WechatOutlined } from "@ant-design/icons";
import { Avatar, Button, Modal, Spin } from "antd";
import dayjs from "dayjs";
import { last } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiFactory from "../../api";
import deagu from "../../assets/deagu.jpg";
import { conversationTab, conversationType } from "../../config/Constant";
import { useAuthLayoutContext } from "../../context/AuthLayoutContext";
import { useMenuContext } from "../../context/MenuContext";
import { useInfoUser } from "../../store/UserStore";
import { useUserPresenceStore } from "../../store/useUserPresenceStore";
import { lastTimeOnlineUtils } from "../../utils/formatTime";
import { updateOrg } from "../../utils/OrganizationHelper";
import { getColorFromInitial } from "../../utils/Utils";
import { useImageLoader } from "../customHook/ImageLoader";
import "./UserDetailModal.scss";
import { UserStatusCustomAvatar } from "./UserStatusCustomAvatar";

const customStyle = {
  borderRadius: "50%",
  position: "absolute",
  left: "90px",
  top: "69px",
  zIndex: 1,
  width: "16px",
  height: "16px",
  border: "2px solid rgb(233, 232, 232)",
};

const UserDetailModal = ({ person, isOpen, handleCancel }) => {
  const navigate = useNavigate();
  const updateUserStatus = useUserPresenceStore(
    (state) => state.updateUserStatus,
  );
  /** Context */
  const { setOrgList } = useAuthLayoutContext();

  const {
    conversationList,
    activeKey,
    handleUpdateConversationList,
    setSelectedConversation,
    setPersonalDetail,
  } = useMenuContext();
  const { user, languageMap } = useInfoUser();
  const [brieftName, setBriefName] = useState();
  const [paths, setPaths] = useState([]);
  const [loadingCreateConversation, setLoadingCreateConversation] =
    useState(false);
  const [partner, setPartner] = useState(person);
  const [countPartnership, setCountPartnership] = useState(0);
  const [errorComponent, setErrorComponent] = useState(<div></div>);
  const {
    imageUrl,
    loading: isImageLoading,
    error: imageError,
  } = useImageLoader(person?.avatar);

  const getPartnerShip = async () => {
    try {
      const rs = await apiFactory.userConversationApi.partnership(
        person?.userId,
      );

      if (!rs || !rs.data || rs.status !== 200) return;

      setCountPartnership(rs.data);
    } catch (error) {}
  };

  const getUserDetailInfo = async () => {
    try {
      if (!person?.userId) return;

      let data = person;

      if (!person?.listOrg?.length) {
        const rs = await apiFactory.userApi.orgUser(person.userId);

        if (!rs?.data || rs?.status !== 200) return;

        data = rs.data;
      }

      updateUserStatus(data?.userId, data?.online ? "ONLINE" : "OFFLINE");
      setPartner(data);
      updateOrg(setOrgList, data);

      const allPaths = data?.listOrg?.flatMap((org) => org?.paths || []);
      setPaths(allPaths);
      setPersonalDetail((prev) => {
        if (prev?.userId === data?.userId) {
          return { ...prev, ...data };
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const getPaths = () => {
    const map = new Map();
    paths.forEach((item) => {
      map.set(item.objectId, { ...item, children: [] });
    });

    paths.forEach((item) => {
      if (item.parentId) {
        map.get(item.parentId)?.children.push(item);
      }
    });

    const result = Array.from(map.values())
      .filter((item) => !item.parentId)
      .map((item) => {
        const allNames = [
          item.name,
          ...item.children.map((child) => child.name),
        ];
        return { name: allNames.join(">") };
      });

    return result;
  };

  const handleCreateConversation = async () => {
    setLoadingCreateConversation(true);

    const userIds =
      user?.userId === person?.userId
        ? [user?.userId]
        : [user?.userId, person?.userId];

    const type =
      user?.userId === person?.userId
        ? conversationType.SELF
        : conversationType.PERSONAL;

    const result = await apiFactory.conversationApi.findGroup({
      name: "",
      userIds: userIds,
      conversationType: type,
    });

    if (result?.data === null) {
      navigate(`/conversation/virtual/${person?.userId}`);
      return;
    }

    if (result?.data?.type === conversationType.PERSONAL) {
      const isExist = conversationList?.some(
        (item) => item?.conversationId === result?.data?.conversationId,
      );

      if (!isExist && activeKey === conversationTab.ALL) {
        handleUpdateConversationList([result?.data, ...conversationList]);
      } else {
        const updatedList = [
          result?.data,
          ...conversationList.filter(
            (item) => item?.conversationId !== result?.data?.conversationId,
          ),
        ];

        handleUpdateConversationList(updatedList);
      }
    }

    setLoadingCreateConversation(false);
    handleCancel();

    navigate(`/conversation/chat/${result?.data?.conversationId}`);
  };

  const comeToMail = (e) => {
    e.stopPropagation();
    setSelectedConversation({ type: "DRAFT" });
    navigate(`/conversation/draft-mail?recipent=${person?.userId}`);
  };

  const generatePath = (pathname, i) => {
    if (!pathname || pathname?.trim()?.length === 0) {
      return (
        <div className="flex items-center">
          <div className={"text-[14px]"}>
            <span className="text-[#d33b3d] p-[2px]">*</span>
            Unknown Organization
          </div>
        </div>
      );
    }

    return (
      <div key={`${pathname}_${i}`} className="flex items-center">
        <div className={"text-[14px]"}>
          <span className="text-[#d33b3d] p-[2px]">*</span>
          {pathname}
        </div>
      </div>
    );
  };

  const getAvatar = useMemo(() => {
    if (isImageLoading) {
      return (
        <Avatar
          className="fb-profile-avatar"
          size={90}
          style={{
            backgroundColor: getColorFromInitial(brieftName),
          }}
          icon={<Spin />}
        >
          {brieftName}
        </Avatar>
      );
    }

    if (imageError) {
      return (
        <Avatar
          className="fb-profile-avatar"
          size={90}
          style={{
            backgroundColor: getColorFromInitial(brieftName),
          }}
        >
          {brieftName}
        </Avatar>
      );
    }

    return (
      <Avatar
        className="fb-profile-avatar"
        size={90}
        src={imageUrl}
        style={{
          backgroundColor: getColorFromInitial(brieftName),
        }}
      >
        {brieftName}
      </Avatar>
    );
  }, [person, imageUrl, isImageLoading, imageError]);

  useEffect(() => {
    if (person) {
      getPartnerShip();
      getUserDetailInfo();

      setBriefName(
        last(person?.name?.trim()?.split(" "))?.[0] ||
          last(person?.username?.trim()?.split(" "))?.[0],
      );
    }
  }, [person]);

  useEffect(() => {
    setErrorComponent(
      <div
        style={{
          width: 90,
          height: 90,
          borderRadius: "50%",
          backgroundColor: getColorFromInitial(brieftName),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 28,
          color: "#fff",
          fontWeight: 500,
          userSelect: "none",
          border: "3px solid white",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        }}
      >
        {brieftName}
      </div>,
    );
  }, [brieftName]);

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={450}
      className="no-padding-modal"
      styles={{
        body: {
          padding: 0,
          backgroundColor: "#f0f2f5",
        },
      }}
    >
      <div className="fb-profile-container">
        <div className="fb-profile-header">
          <img src={deagu} alt="Cover" className="fb-cover-photo" />
        </div>

        <div className="fb-profile-content">
          <div className="fb-profile-identity">
            {getAvatar}
            <UserStatusCustomAvatar
              customStyle={customStyle}
              userId={person?.userId}
            />
            <div className="fb-profile-name-wrapper">
              <h1 className="fb-user-name">{person.name || "UNKNOWN"}</h1>
              <span className="fb-last-time-online">
                {!partner?.online &&
                  lastTimeOnlineUtils(partner, languageMap, user?.language)}
              </span>
            </div>
          </div>

          <div className="fb-profile-actions">
            <Button
              type="primary"
              size="large"
              block
              loading={loadingCreateConversation}
              onClick={handleCreateConversation}
            >
              {languageMap?.["modal.detail.bnt.sendMessage"] ?? "Send message"}
            </Button>
            <Button
              type="primary"
              size="large"
              block
              disabled={loadingCreateConversation}
              onClick={comeToMail}
            >
              {languageMap?.["modal.detail.bnt.sendMail"] ?? "Send mail"}
            </Button>
          </div>

          <div className="fb-info-section">
            <h3 className="fb-section-title">
              {languageMap?.["modal.detail.label.userInfo"] ??
                "User information"}
            </h3>
            <div className="fb-info-row">
              <span>
                {languageMap?.["modal.detail.label.userCode"] ?? "User id"}
              </span>
              <span>{partner?.userId || "..."}</span>
            </div>
            <div className="fb-info-row">
              <span>
                {languageMap?.["menu.profile.position"] ?? "Position"}
              </span>
              <span>{partner?.position || "..."}</span>
            </div>
            <div className="fb-info-row">
              <span>
                {languageMap?.["modal.detail.label.phone"] ?? "Phone"}
              </span>
              <span>{partner?.phone || "..."}</span>
            </div>
            <div className="fb-info-row">
              <span>{languageMap?.["modal.detail.label.mood"] ?? "Mood"}</span>
              <span>{partner?.mood || "..."}</span>
            </div>
            <div className="fb-info-row">
              <span>
                {languageMap?.["modal.detail.label.birthday"] ?? "Birthday"}
              </span>
              <span>
                {partner?.birthday
                  ? dayjs(partner?.birthday).format("YYYY-MM-DD")
                  : "..."}
              </span>
            </div>
            <div className="fb-info-row">
              <span>
                {languageMap?.["modal.detail.label.email"] ?? "Email"}
              </span>
              <span>{partner?.email || "..."}</span>
            </div>
          </div>

          <div className="fb-info-section">
            <h3 className="fb-section-title">
              {languageMap?.["modal.detail.label.org"] ?? "Organization"}
            </h3>
            <div className="fb-no-photos">
              {getPaths()?.map((path, j) => generatePath(path?.name, j))}
            </div>
          </div>
          <div className="fb-info-option">
            <button
              className="fb-menu-option"
              onClick={() => {
                /* Xử lý sự kiện xem nhóm chung */
              }}
            >
              <WechatOutlined className="fb-menu-icon" />
              <span className="fb-menu-text">
                {languageMap?.["modal.detail.label.generalChatGr"] ??
                  "General chat group"}{" "}
                ({countPartnership?.conversationCount || 0})
              </span>
            </button>

            <button
              className="fb-menu-option"
              onClick={() => {
                /* Xử lý sự kiện chia sẻ danh thiếp */
              }}
            >
              <MailOutlined className="fb-menu-icon" />
              <span className="fb-menu-text">
                {languageMap?.["modal.detail.label.generalMessageGr"] ??
                  "General message group"}{" "}
                ({countPartnership?.messageCount || 0})
              </span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { UserDetailModal };
